import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { ArrowRight, Loader2, Building2, UserRound } from "lucide-react";

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("pharmacy");
  const [form, setForm] = useState({
    email: "", password: "", name: "",
    pharmacy_name: "", address: "", phone: "",
    professional_role: "Pharmacien", experience_years: 0, bio: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await register({ ...form, role, experience_years: parseInt(form.experience_years) || 0, skills: [] });
      navigate(u.role === "pharmacy" ? "/dashboard" : "/profile/edit?step=0");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8F6] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link to="/login" className="flex items-center gap-2" data-testid="back-to-login">
            <img src="/apocia-logo.png" alt="Apocia" className="h-10 w-auto" data-testid="register-logo" />
          </Link>
          <LanguageSwitcher />
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-3xl border border-[#E2E5E0] p-8 sm:p-10 shadow-sm"
          data-testid="register-form"
        >
          <h1 className="text-3xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>
            {t("auth.register")}
          </h1>
          <p className="text-sm text-[#6B726A] mt-2 mb-8">
            {t("auth.tagline_sub")}
          </p>

          <div className="grid grid-cols-2 gap-3 mb-8">
            <button
              type="button"
              data-testid="role-pharmacy-button"
              onClick={() => setRole("pharmacy")}
              className={`flex items-center gap-3 px-4 py-4 rounded-2xl border text-left transition-all ${
                role === "pharmacy"
                  ? "border-[#1A4D2E] bg-[#E8F0EA]"
                  : "border-[#E2E5E0] bg-white hover:border-[#1A4D2E]/30"
              }`}
            >
              <Building2 className="w-5 h-5 text-[#1A4D2E]" />
              <div>
                <div className="font-medium text-[#1A4D2E]">{t("auth.i_am_pharmacy")}</div>
                <div className="text-xs text-[#6B726A]">{t("auth.looking_replacement")}</div>
              </div>
            </button>
            <button
              type="button"
              data-testid="role-replacement-button"
              onClick={() => setRole("replacement")}
              className={`flex items-center gap-3 px-4 py-4 rounded-2xl border text-left transition-all ${
                role === "replacement"
                  ? "border-[#1A4D2E] bg-[#E8F0EA]"
                  : "border-[#E2E5E0] bg-white hover:border-[#1A4D2E]/30"
              }`}
            >
              <UserRound className="w-5 h-5 text-[#1A4D2E]" />
              <div>
                <div className="font-medium text-[#1A4D2E]">{t("auth.i_am_replacement")}</div>
                <div className="text-xs text-[#6B726A]">{t("auth.looking_mission")}</div>
              </div>
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("auth.name")} required>
              <input data-testid="register-name-input" required className="rp-input" value={form.name} onChange={upd("name")} />
            </Field>
            <Field label={t("auth.email")} required>
              <input data-testid="register-email-input" type="email" required className="rp-input" value={form.email} onChange={upd("email")} />
            </Field>
            <Field label={t("auth.password") + " (min 6)"} required>
              <input data-testid="register-password-input" type="password" minLength={6} required className="rp-input" value={form.password} onChange={upd("password")} />
            </Field>
            <Field label={t("auth.phone")}>
              <input className="rp-input" value={form.phone} onChange={upd("phone")} />
            </Field>

            {role === "pharmacy" ? (
              <>
                <Field label={t("auth.pharmacy_name")}>
                  <input data-testid="register-pharmacy-name-input" className="rp-input" value={form.pharmacy_name} onChange={upd("pharmacy_name")} />
                </Field>
                <Field label={t("auth.address")}>
                  <input className="rp-input" value={form.address} onChange={upd("address")} />
                </Field>
              </>
            ) : (
              <>
                <Field label={t("auth.role")}>
                  <select data-testid="register-professional-role-select" className="rp-input" value={form.professional_role} onChange={upd("professional_role")}>
                    <option>Pharmacien</option>
                    <option>Adjoint</option>
                    <option>Préparateur</option>
                  </select>
                </Field>
                <Field label={t("auth.experience")}>
                  <input type="number" min={0} className="rp-input" value={form.experience_years} onChange={upd("experience_years")} />
                </Field>
                <Field label={t("auth.bio")} full>
                  <textarea rows={3} className="rp-input resize-none" value={form.bio} onChange={upd("bio")} />
                </Field>
              </>
            )}
          </div>

          {err && (
            <div data-testid="register-error" className="text-sm text-[#E3655B] bg-[#FFF4F3] border border-[#E3655B]/20 rounded-xl px-3 py-2 mt-4">
              {err}
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            <Link to="/login" className="text-sm text-[#6B726A] hover:text-[#1A4D2E]">
              {t("auth.login_cta")} {t("auth.login")}
            </Link>
            <button data-testid="register-submit-button" disabled={busy} className="rp-btn-primary flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {t("auth.create_account")}
            </button>
          </div>
        </form>
      </div>

      <style>{`.rp-input{width:100%;background:#fff;border:1px solid #E2E5E0;border-radius:.75rem;padding:.65rem .9rem;outline:none;transition:all .2s}.rp-input:focus{border-color:#1A4D2E;box-shadow:0 0 0 3px rgba(26,77,46,.12)}`}</style>
    </div>
  );
}

function Field({ label, required, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-wider font-semibold text-[#6B726A]">
        {label}{required && <span className="text-[#E3655B]"> *</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
